import { Injectable, computed, signal } from '@angular/core';

export type RhsPanelState = 'open' | 'closed';

/**
 * Tracks the side panels that take width away from a page's main content.
 * Neither changes the viewport, so a media query cannot see them; anything that
 * shrinks with the content column reads their state from here.
 */
@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  private readonly rhsPanelState = signal<RhsPanelState>('closed');
  private readonly filtersPanelState = signal<RhsPanelState>('closed');

  /** Right-hand side panel state — set from pages hosting a detail / split layout. */
  readonly rhsPanel = this.rhsPanelState.asReadonly();

  /** Filters sidebar state — kept in step with `BaseGridPage.showFilters`. */
  readonly filtersPanel = this.filtersPanelState.asReadonly();

  /** True while either panel is out, i.e. the main content is running narrow. */
  readonly sidePanelOpen = computed(
    () => this.rhsPanel() === 'open' || this.filtersPanel() === 'open',
  );

  /** True while both are out, which leaves the content column very little room. */
  readonly bothSidePanelsOpen = computed(
    () => this.rhsPanel() === 'open' && this.filtersPanel() === 'open',
  );

  setRhsPanel(state: RhsPanelState): void {
    this.rhsPanelState.set(state);
  }

  openRhsPanel(): void {
    this.setRhsPanel('open');
  }

  closeRhsPanel(): void {
    this.setRhsPanel('closed');
  }

  setFiltersPanel(state: RhsPanelState): void {
    this.filtersPanelState.set(state);
  }
}
